import { useEffect, useState, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { apiStore } from '@/stores/ApiStore';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { PaymentError } from '@/components/ui/PaymentError';
import { CtaButton } from '@/components/ui/CtaButton';
import { PlanCard } from '@/components/paywall/PlanCard';
import { NoPay } from '@/components/paywall/NoPay';
import { PaywallHeader } from '@/components/paywall/PaywallHeader';
import { AccountLayout } from '@/components/ui/AccountLayout';
import { AppStoreDownloadBadge } from '@/components/ui/AppStoreDownloadBadge';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import {
  sendFailed,
  sendPaymentCancelled,
} from '@/hooks/useWebViewMessaging';
import { useSessionSubscriptionCheckout } from '@/hooks/useSessionSubscriptionCheckout';

const WrapTitle = styled.div`
  margin-bottom: 28px;
`;

const Plans = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 16px;
`;

const NextBilling = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #6b6b6b;
`;

const CurrentPlanLabel = styled.div`
  font-size: 16px;
  margin-bottom: 12px;
  letter-spacing: 0.2px;
`;

const ContentBottom = styled.div`
  margin-top: auto;
  margin-bottom: 24px;
`;

const Cta = styled.section`
  margin-top: 18px;
`;

const Meta = styled.div`
  display: grid;
  justify-content: center;
  margin: 8px 0 0 0;
`;

const CurrentPlanContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const statusLabelColors = {
  active: '#22c55e',
  canceled: '#6b7280',
  pastDue: '#f59e0b',
  trial: '#31bcf9',
} as const;

type SubscriptionLabelTone = keyof typeof statusLabelColors;

const StatusLabel = styled.span<{ $tone: SubscriptionLabelTone }>`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: ${({ $tone }) => statusLabelColors[$tone]};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-box: trim-both cap alphabetic;
  z-index: 1;
`;

const getSubscriptionLabel = (
  status: string,
): { text: string; tone: SubscriptionLabelTone } | null => {
  switch (status) {
    case 'active':
      return { text: 'ACTIVE', tone: 'active' };
    case 'canceled':
      return { text: 'CANCELED', tone: 'canceled' };
    case 'past_due':
      return { text: 'PAST DUE', tone: 'pastDue' };
    case 'trial':
      return { text: 'TRIAL', tone: 'trial' };
    default:
      return null;
  }
};

const utcDateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const formatUtcDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : utcDateFormatter.format(date);
};

const getCancellationErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = error.response;
    if (
      response
      && typeof response === 'object'
      && 'status' in response
    ) {
      const status = Number(response.status);
      if (status === 409) {
        return 'The subscription state changed. Refresh the page and try canceling again.';
      }
      if (status >= 500) {
        return 'We could not confirm cancellation with the payment provider. Please try again.';
      }
    }
  }

  if (
    error instanceof Error
    && error.message.includes('not confirmed')
  ) {
    return 'The server did not confirm the cancellation. Please try again.';
  }

  return 'Unable to cancel the subscription. Check your connection and try again.';
};

export const AccountSubscription = observer(() => {
  const navigate = useNavigate();
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const cancelInFlight = useRef(false);
  const {
    activate: activateSessionSubscription,
    available: sessionCheckoutAvailable,
    error: sessionActivationError,
    processing: sessionProcessing,
  } = useSessionSubscriptionCheckout('account');
  const isSessionCheckout = sessionCheckoutAvailable
    && apiStore.user?.checkoutMode === 'session';

  // Фильтрация планов
  const filteredPlans = useMemo(
    () =>
      subscriptionStore.plans?.filter(
        (plan) => !plan.planId.includes('discount') && !plan.trial,
      ) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- MobX store: component re-renders via observer
    [subscriptionStore.plans],
  );

  // Проверка наличия планов
  const hasPlans = useMemo(() => filteredPlans.length > 0, [filteredPlans]);

  const hasAccess = subscriptionStore.hasAccess;
  const canCancel = hasAccess && subscriptionStore.status?.status !== 'canceled';
  const showCta = canCancel || (!hasAccess && hasPlans);
  const statusLabel = subscriptionStore.status
    ? getSubscriptionLabel(subscriptionStore.status.status)
    : null;

  // Инициализация при монтировании
  useEffect(() => {
    const init = async () => {
      await subscriptionStore.fetchStatus();
      if (!subscriptionStore.hasAccess) {
        await subscriptionStore.fetchPlans();
        const plans
          = subscriptionStore.plans?.filter(
            (p) => !p.planId.includes('discount') && !p.trial,
          ) || [];
        setSelectedPlanId(plans[0]?.planId || '');
      }
    };
    void init();
  }, []);

  // Отслеживание изменений доступа (аналог watch в Vue)
  useEffect(() => {
    const handleHasAccessChange = () => {
      if (!subscriptionStore.hasAccess && !subscriptionStore.loading && filteredPlans.length > 0) {
        setSelectedPlanId(filteredPlans[0]?.planId || '');
      }
    };
    handleHasAccessChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- MobX store: subscriptionStore used for condition
  }, [subscriptionStore.hasAccess, subscriptionStore.loading, filteredPlans]);

  // Обработка перехода к оплате
  const handleProceed = () => {
    if (!selectedPlanId) return;
    if (isSessionCheckout) {
      void activateSessionSubscription(selectedPlanId);
      return;
    }
    navigate(`${ROUTES.PAYMENT.INDEX}?planId=${selectedPlanId}`);
  };

  // Обработка отмены подписки
  const handleCancel = async () => {
    if (cancelInFlight.current) return;
    cancelInFlight.current = true;

    try {
      setProcessing(true);
      setCancelError('');

      const canceledSubscription = await apiStore.cancelSubscription();
      subscriptionStore.applyStatus(canceledSubscription);

      sendPaymentCancelled();
    }
    catch (error) {
      const errorMessage = getCancellationErrorMessage(error);
      setCancelError(errorMessage);
      sendFailed(errorMessage);
    }
    finally {
      cancelInFlight.current = false;
      setProcessing(false);
    }
  };

  // Переход к настройкам
  const toGear = () => {
    navigate(ROUTES.POLICY.INDEX);
  };

  // Показываем загрузку
  if (subscriptionStore.loading) {
    return <LoadingSpinner />;
  }

  return (
    <AccountLayout showLogo showGear showHome onGear={toGear}>
      <PaywallHeader>
        <WrapTitle>Subscription</WrapTitle>
      </PaywallHeader>

      {hasAccess && subscriptionStore.status
        ? (
            <div>
              <CurrentPlanLabel>Your current plan</CurrentPlanLabel>
              <CurrentPlanContainer>
                <PlanCard
                  shadow
                  selected
                  noRadio
                  name={`${subscriptionStore.status.plan.billingFrequency.toUpperCase()}`}
                  price={`$${subscriptionStore.status.plan.price} / ${subscriptionStore.status.plan.billingFrequency}`}
                />
                {statusLabel && (
                  <StatusLabel $tone={statusLabel.tone}>{statusLabel.text}</StatusLabel>
                )}
              </CurrentPlanContainer>
              <NextBilling>
                {subscriptionStore.status.status === 'canceled'
                  ? (
                      <>
                        Access ends:
                        {' '}
                        {formatUtcDate(subscriptionStore.status.accessUntil)}
                      </>
                    )
                  : (
                      <>
                        Next billing:
                        {' '}
                        {formatUtcDate(subscriptionStore.status.nextBillingDate)}
                      </>
                    )}
              </NextBilling>
              <AppStoreDownloadBadge
                description="Use your subscription in the Shieldix app"
              />
            </div>
          )
        : (
            <Plans>
              {filteredPlans.map((plan) => (
                <PlanCard
                  key={plan.planId}
                  best={!!plan.trial}
                  shadow
                  selected={selectedPlanId === plan.planId}
                  onSelect={() => setSelectedPlanId(plan.planId)}
                  name={`${plan.billingFrequency.toUpperCase()}`}
                  price={
                    plan.discount
                      ? (
                          `$${plan.discount.discountedPrice} first ${plan.discount.durationCycles} cycle(s), then $${plan.price}`
                        )
                      : (
                          `$${plan.price}`
                        ) + ` / ${plan.billingFrequency}`
                  }
                  description={plan.description}
                />
              ))}
            </Plans>
          )}

      <ContentBottom>
        {showCta && (
          <Cta>
            {canCancel
              ? (
                  <>
                    <CtaButton
                      loading={processing}
                      disabled={processing}
                      onClick={handleCancel}
                    >
                      Cancel Subscription
                    </CtaButton>
                    <PaymentError error={cancelError} />
                  </>
                )
              : (
                  <CtaButton
                    loading={processing || sessionProcessing}
                    disabled={!selectedPlanId || processing || sessionProcessing}
                    onClick={handleProceed}
                  >
                    Continue
                  </CtaButton>
                )}
            {!canCancel && (
              <PaymentError error={sessionActivationError} />
            )}
          </Cta>
        )}

        {!hasAccess && (
          <Meta>
            <NoPay trialEnabled={false} />
          </Meta>
        )}
      </ContentBottom>
    </AccountLayout>
  );
});
